import type {
  Assignee,
  CloseResult,
  Command,
  CommandResult,
  DoneCheck,
  Evidence,
  GraphCommand,
  Health,
  Item,
  ItemPatch,
  MemoryDraft,
  MemoryItem,
  Mind,
  Monitor,
  PolicyDocument,
  Principal,
  ProjectState,
  Proposal,
  Resolution,
  WorkerSettings,
} from "@minga/core"
import { ALREADY_UNDONE, mintId, refMissHint, versionsOf } from "@minga/core"
import type { Cadence, RepoBaseBranch } from "@minga/protocol"

/**
 * The writes the web issues (spec §7.6 steps 5 and 7). Plan edits are `SubmitChangeset`s (every edit is a changeset,
 * so plan versions and undo have one unit); flow actions (retry, pause) are top-level commands. Ids are minted on the
 * client with core `mintId`, so an optimistic node keeps its id when the server confirms it.
 */

export type NewId = (prefix: string) => string

export const newId: NewId = (prefix) => {
  const bytes = new Uint8Array(10)
  globalThis.crypto.getRandomValues(bytes)
  return mintId(prefix, Date.now(), bytes)
}

export const slugify = (name: string): string =>
  name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "area"

/** A slug not used by any area of the project: "growth", "growth-2", … */
export const uniqueAreaSlug = (state: ProjectState, name: string): string => {
  const base = slugify(name)
  const taken = new Set(state.areas.map((a) => a.slug))
  if (!taken.has(base)) return base
  for (let n = 2; ; n++) if (!taken.has(`${base}-${n}`)) return `${base}-${n}`
}

const changeset = (
  id: NewId,
  rationale: string,
  changes: readonly [GraphCommand, ...GraphCommand[]],
): Command => ({
  _tag: "SubmitChangeset",
  changesetId: id("chg") as never,
  rationale,
  changes,
  onPropose: "apply_allowed",
})

export const addAreaCommand = (
  state: ProjectState,
  input: { readonly name: string; readonly color: string; readonly mission: string },
  id: NewId = newId,
): Command =>
  changeset(id, `Add the ${input.name.trim()} area`, [
    {
      _tag: "CreateArea",
      areaId: id("area") as never,
      slug: uniqueAreaSlug(state, input.name),
      name: input.name.trim(),
      color: input.color,
      charter: { mission: input.mission.trim(), body: "" },
    },
  ])

/** A goal a person judges (target null) with its criteria ("done when"), one per line. */
export const newGoalCommand = (
  input: { readonly title: string; readonly why: string; readonly doneWhen: ReadonlyArray<string> },
  id: NewId = newId,
): Command => {
  const doneWhen = input.doneWhen.map((d) => d.trim()).filter(Boolean)
  return changeset(id, `New goal: ${input.title.trim()}`, [
    {
      _tag: "CreateGoal",
      goalId: id("goal") as never,
      title: input.title.trim(),
      why: input.why.trim(),
      target: null,
      criteria: doneWhen.length ? doneWhen : [input.title.trim()],
    },
  ])
}

/** Decision 0097: set (or clear, with an empty text) how a KPI's number is fetched. */
export const kpiRecipeCommand = (kpiId: string, recipe: string, id: NewId = newId): Command => {
  const text = recipe.trim()
  return changeset(id, text ? "Set the KPI's recipe" : "Remove the KPI's recipe", [
    { _tag: "UpdateKpi", kpiId: kpiId as never, patch: { recipe: text ? text : null } },
  ])
}

export const changeMindCommand = (agentId: string, mind: Mind, id: NewId = newId): Command =>
  changeset(id, "Change the agent's mind", [{ _tag: "ChangeAgentMind", agentId: agentId as never, mind }])

// ---- items (decision 0035) ------------------------------------------------------------------------------------------

/** An intent key from a title: "Export fails for emoji file names" → "export-fails-for-emoji-file-names-k3x9". */
export const intentKeyOf = (title: string, salt: string): string => {
  const base = title
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
  const tail = salt
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .slice(-6)
  return `${base || "item"}-${tail || "0"}`.replace(/^-+/, "")
}

export interface NewItemInput {
  readonly title: string
  readonly why?: string
  readonly body?: string
  readonly type?: string
  readonly parentId?: string | null
  readonly areaId?: string | null
  readonly goalIds?: ReadonlyArray<string>
  readonly owner?: Principal | null
  readonly assignee?: Assignee
  readonly status?: "triage" | "backlog" | "todo"
  readonly checks?: ReadonlyArray<DoneCheck>
  readonly dueAt?: number | null
}

/** The CreateItem change of a new item. Omitted values get decide's defaults (owner: the agent above, never you). */
export const createItemChange = (
  input: NewItemInput,
  id: NewId = newId,
): Extract<GraphCommand, { _tag: "CreateItem" }> => {
  const itemId = id("itm")
  const title = input.title.trim()
  return {
    _tag: "CreateItem",
    itemId: itemId as never,
    title,
    why: (input.why ?? "").trim() || title,
    intentKey: intentKeyOf(title, itemId) as never,
    ...(input.body?.trim() ? { body: input.body.trim() as never } : {}),
    ...(input.type ? { type: input.type as never } : {}),
    ...(input.parentId ? { parentId: input.parentId as never } : {}),
    ...(input.areaId ? { areaId: input.areaId as never } : {}),
    ...(input.goalIds && input.goalIds.length > 0 && !input.parentId
      ? { goalIds: input.goalIds as never }
      : {}),
    ...(input.owner !== undefined ? { owner: input.owner } : {}),
    ...(input.assignee !== undefined ? { assignee: input.assignee } : {}),
    ...(input.status ? { status: input.status } : {}),
    ...(input.checks ? { checks: input.checks } : {}),
    ...(input.dueAt ? { dueAt: input.dueAt as never } : {}),
  }
}

export const createItemCommand = (input: NewItemInput, id: NewId = newId): Command =>
  changeset(id, `New item: ${input.title.trim()}`, [createItemChange(input, id)])

/** Capture into Triage (§6.4): a person's capture from the web, clean. */
export const captureCommand = (
  input: { readonly title: string; readonly body?: string; readonly type: string },
  id: NewId = newId,
): Command =>
  changeset(id, `Capture: ${input.title.trim()}`, [
    {
      ...createItemChange(
        {
          title: input.title,
          why: "Captured from the web",
          type: input.type,
          status: "triage",
          ...(input.body ? { body: input.body } : {}),
        },
        id,
      ),
      source: { _tag: "web" },
    },
  ])

export const updateItemCommand = (
  itemId: string,
  patch: ItemPatch,
  rationale = "Update the item",
  id: NewId = newId,
): Command => changeset(id, rationale, [{ _tag: "UpdateItem", itemId: itemId as never, patch }])

/** A planning status and/or a new parent (Planning / Ready / Now, Move…). */
export const moveItemCommand = (
  itemId: string,
  to: { readonly status?: "triage" | "backlog" | "todo" | "in_progress"; readonly parentId?: string | null },
  id: NewId = newId,
): Command =>
  changeset(id, to.status ? `Move to ${to.status.replace(/_/g, " ")}` : "Move the item", [
    {
      _tag: "MoveItem",
      itemId: itemId as never,
      ...(to.status ? { status: to.status } : {}),
      ...(to.parentId !== undefined ? { parentId: to.parentId as never } : {}),
    },
  ])

/** Decision 0084 §7.3: "Move to Ready" (Planning → Ready), a MoveItem to `todo`. */
export const moveToReadyCommand = (itemId: string, id: NewId = newId): Command =>
  changeset(id, "Move to Ready", [{ _tag: "MoveItem", itemId: itemId as never, status: "todo" }])

/** Owner and/or assignee. */
export const assignItemCommand = (
  itemId: string,
  to: { readonly owner?: Principal | null; readonly assignee?: Assignee | null },
  id: NewId = newId,
): Command =>
  changeset(id, "Change who answers for or does the item", [
    {
      _tag: "AssignItem",
      itemId: itemId as never,
      ...(to.owner !== undefined ? { owner: to.owner } : {}),
      ...(to.assignee !== undefined ? { assignee: to.assignee } : {}),
    },
  ])

/**
 * Close an item (CloseItem, a plan edit). `done`: Mark done (evidence required only for command or artifact checks;
 * core checks it) or closing a container; `cancelled`: won't do, duplicate of…, superseded by…, impossible.
 */
export const closeItemCommand = (
  itemId: string,
  as: "done" | "cancelled",
  note: string,
  opts: {
    readonly evidence?: ReadonlyArray<Evidence>
    readonly resolution?: Resolution
    /** Decision 0101: `done` only: what it delivered (absent = verified). */
    readonly result?: CloseResult
  } = {},
  id: NewId = newId,
): Command =>
  changeset(id, as === "done" ? `Mark done: ${note}` : `Cancel: ${note}`, [
    {
      _tag: "CloseItem",
      itemId: itemId as never,
      as,
      note,
      ...(opts.evidence && opts.evidence.length > 0 ? { evidence: opts.evidence as never } : {}),
      ...(opts.resolution ? { resolution: opts.resolution } : {}),
      ...(as === "done" && opts.result ? { result: opts.result } : {}),
    },
  ])

/** Mark done (a person's leaf, decision 0032): CloseItem{done} with the picked evidence. */
export const markDoneCommand = (
  itemId: string,
  note: string,
  evidence: ReadonlyArray<Evidence>,
  id: NewId = newId,
): Command => closeItemCommand(itemId, "done", note, { evidence }, id)

/** Cancel as won't do. */
export const cancelItemCommand = (itemId: string, reason: string, id: NewId = newId): Command =>
  closeItemCommand(itemId, "cancelled", reason, { resolution: { _tag: "wont_do" } }, id)

/** Run now (D4): one occurrence of a recurring item, as its child. */
export const runOccurrenceCommand = (itemId: string, id: NewId = newId): Command =>
  changeset(id, "Run now", [
    { _tag: "RunOccurrence", itemId: itemId as never, occurrenceId: id("itm") as never },
  ])

export const reviseChecksCommand = (
  itemId: string,
  checks: ReadonlyArray<DoneCheck>,
  id: NewId = newId,
): Command => changeset(id, "Revise the checks", [{ _tag: "ReviseChecks", itemId: itemId as never, checks }])

/** Remove the `blocks` edges from these (cancelled) predecessors into the item or an item above it. */
export const removeDependenciesCommand = (
  state: ProjectState,
  itemId: string,
  fromIds: ReadonlyArray<string>,
  id: NewId = newId,
): Command | null => {
  const above = new Set<string>([itemId])
  let cur = state.items.find((t) => t.id === itemId)
  while (cur?.parentId) {
    above.add(cur.parentId)
    cur = state.items.find((t) => t.id === cur?.parentId)
  }
  const from = new Set(fromIds)
  const edges = state.edges.filter((e) => e.kind === "blocks" && from.has(e.from.id) && above.has(e.to.id))
  const [first, ...rest] = edges.map((e): GraphCommand => ({ _tag: "RemoveEdge", edgeId: e.id }))
  return first ? changeset(id, "Remove the dependency", [first, ...rest]) : null
}

export const retryItemCommand = (itemId: string, note?: string): Command => ({
  _tag: "RetryItem",
  itemId: itemId as never,
  ...(note ? { note } : {}),
})

export const pauseItemCommand = (itemId: string, paused: boolean, note?: string): Command => ({
  _tag: "PauseItem",
  itemId: itemId as never,
  paused,
  ...(note ? { note } : {}),
})

// ---- lead control wave (decision 0032): close as done, accept impossible, unblock --------------------------------

/** Accept a worker's impossible claim: the item closes cancelled with resolution impossible (a good outcome). */
export const acceptImpossibleCommand = (itemId: string, note: string): Command => ({
  _tag: "AcceptImpossible",
  itemId: itemId as never,
  note,
})

/** Unblock a blocked item; `hold` also pauses it, so no new attempt starts. */
export const unblockItemCommand = (itemId: string, note: string, hold = false): Command => ({
  _tag: "UnblockItem",
  itemId: itemId as never,
  note,
  ...(hold ? { hold: true } : {}),
})

/** A note or an update (with health) on a goal or an item. */
export const postCommentCommand = (
  target: { readonly _tag: "item" | "goal"; readonly id: string },
  body: string,
  health?: Health,
  id: NewId = newId,
): Command => ({
  _tag: "PostComment",
  commentId: id("cmt") as never,
  target: target as never,
  threadId: null,
  body: body as never,
  ...(health ? { health } : {}),
})

/** Attach a link to an item (PostArtifact{kind: link}). */
export const attachLinkCommand = (
  itemId: string,
  input: { readonly title: string; readonly uri: string },
  id: NewId = newId,
): Command => ({
  _tag: "PostArtifact",
  artifactId: id("art") as never,
  itemId: itemId as never,
  kind: "link",
  title: input.title.trim() || input.uri.trim(),
  uri: input.uri.trim(),
  contentType: null,
  sizeBytes: null,
  sha256: null,
  preview: null,
  interfaceKey: null,
  supersedes: null,
})

/**
 * Attach an uploaded file to an item (decision 0050): PostArtifact with the upload's facts (the server checks them
 * against its upload record).
 */
export const attachFileCommand = (
  itemId: string,
  input: {
    readonly kind: Extract<Command, { _tag: "PostArtifact" }>["kind"]
    readonly title: string
    readonly uri: string
    readonly contentType: string
    readonly sizeBytes: number
    readonly sha256: string
  },
  id: NewId = newId,
): Command => ({
  _tag: "PostArtifact",
  artifactId: id("art") as never,
  itemId: itemId as never,
  kind: input.kind,
  title: input.title,
  uri: input.uri,
  contentType: input.contentType,
  sizeBytes: input.sizeBytes,
  sha256: input.sha256 as never,
  preview: null,
  interfaceKey: null,
  supersedes: null,
})

/** A person's ✕ (rejected), Undo (draft) or pick (final) on an artifact. */
export const setArtifactStateCommand = (
  artifactId: string,
  state: "draft" | "final" | "rejected",
): Command => ({ _tag: "SetArtifactState", artifactId: artifactId as never, state })

/**
 * Wave DOC (decision 0068): post a project document, or a new version of one (`supersedes`: the live head). A file
 * document carries its upload's facts; a link document's `uri` is its `doc.url` (kind `link`). Always project-level.
 */
export const postDocumentCommand = (
  input: {
    readonly kind: Extract<Command, { _tag: "PostArtifact" }>["kind"]
    readonly title: string
    readonly uri: string
    readonly contentType: string | null
    readonly sizeBytes: number | null
    readonly sha256: string | null
    /** The notes (a link's, or any document's), stored in `preview`. */
    readonly notes?: string | null | undefined
    readonly path: string | null
    readonly url: string | null
    readonly home?: "minga" | "external" | undefined
    readonly sourceRevision?: string | undefined
    readonly notebookFor?: string | undefined
    readonly supersedes?: string | null | undefined
    /** The bytes came from outside the team (decision 0050): taint only rises. */
    readonly untrusted?: boolean | undefined
  },
  id: NewId = newId,
): Command => ({
  _tag: "PostArtifact",
  artifactId: id("art") as never,
  itemId: null,
  kind: input.kind,
  title: input.title,
  uri: input.uri,
  contentType: input.contentType,
  sizeBytes: input.sizeBytes,
  sha256: input.sha256 as never,
  preview: input.notes?.trim() ? input.notes.trim() : null,
  interfaceKey: null,
  supersedes: (input.supersedes ?? null) as never,
  doc: {
    path: input.path as never,
    url: input.url as never,
    ...(input.home !== undefined ? { home: input.home } : {}),
    ...(input.sourceRevision !== undefined ? { sourceRevision: input.sourceRevision } : {}),
    ...(input.notebookFor !== undefined ? { notebookFor: input.notebookFor as never } : {}),
  },
  ...(input.untrusted ? { untrusted: true } : {}),
})

/** Remove one version of a document (decision 0068): `rejected`; its bytes stay (Purge drops them). */
export const removeDocumentCommand = (artifactId: string): Command =>
  setArtifactStateCommand(artifactId, "rejected")

/**
 * Remove a document: every version of its chain that is not rejected yet, newest first. Rejecting only the head would
 * make the version before it live again (an older version is live once nothing live supersedes it), so the path would
 * not be free.
 */
export const removeDocumentCommands = (
  s: Pick<ProjectState, "artifacts">,
  head: ProjectState["artifacts"][number],
): ReadonlyArray<Command> =>
  versionsOf(s, head)
    .filter((v) => v.state !== "rejected")
    .reverse()
    .map((v) => removeDocumentCommand(v.id))

/**
 * Restore an older version of a document: a new version as the person with the old version's bytes (or link), title
 * and notes, on top of the live head. It shows as "Restored v2 by …" (core `docByline`). A tainted version stays
 * tainted; an agent's bytes keep scripts off (core `viewOf` checks where the bytes came from, not only who restored).
 */
export const restoreDocumentCommand = (
  old: Pick<
    ProjectState["artifacts"][number],
    "kind" | "title" | "uri" | "contentType" | "sizeBytes" | "sha256" | "preview" | "doc" | "tainted"
  >,
  head: Pick<ProjectState["artifacts"][number], "id" | "doc">,
  id: NewId = newId,
): Command =>
  postDocumentCommand(
    {
      kind: old.kind,
      title: old.title,
      uri: old.uri,
      contentType: old.contentType,
      sizeBytes: old.sizeBytes,
      sha256: old.sha256,
      notes: old.preview,
      path: head.doc?.path ?? old.doc?.path ?? null,
      // a link's url is what it restores; a file keeps the head's live source
      url: old.sha256 === null ? (old.doc?.url ?? old.uri) : (head.doc?.url ?? null),
      home: head.doc?.home ?? old.doc?.home,
      sourceRevision: old.doc?.sourceRevision,
      supersedes: head.id,
      untrusted: old.tainted,
    },
    id,
  )

export interface AcceptInput {
  readonly type: string
  readonly areaId: string | null
  readonly parentId: string | null
  readonly owner: Principal | null
  readonly assignee: Assignee | null
  readonly status: "backlog" | "todo" | "in_progress"
}

/**
 * Accept a triage item (§6.4): one changeset of UpdateItem (type, area), MoveItem (parent), AssignItem (owner,
 * assignee) and MoveItem (the status), in the order decide needs them (owner and area before `todo`).
 */
export const acceptChanges = (item: Item, input: AcceptInput): GraphCommand[] => {
  const itemId = item.id
  const changes: GraphCommand[] = []
  const patch: { -readonly [K in keyof ItemPatch]: ItemPatch[K] } = {}
  if (input.type !== item.type) patch.type = input.type as never
  if (input.areaId !== item.areaId) patch.areaId = input.areaId as never
  if (Object.keys(patch).length > 0) changes.push({ _tag: "UpdateItem", itemId, patch })
  if (input.parentId !== item.parentId)
    changes.push({ _tag: "MoveItem", itemId, parentId: input.parentId as never })
  const ownerChanged = JSON.stringify(input.owner) !== JSON.stringify(item.owner)
  const assigneeChanged = JSON.stringify(input.assignee) !== JSON.stringify(item.assignee)
  if (ownerChanged || assigneeChanged)
    changes.push({
      _tag: "AssignItem",
      itemId,
      ...(ownerChanged ? { owner: input.owner } : {}),
      ...(assigneeChanged ? { assignee: input.assignee } : {}),
    })
  if (input.status !== item.status) changes.push({ _tag: "MoveItem", itemId, status: input.status })
  return changes
}

export const acceptCommand = (
  items: ReadonlyArray<{ readonly item: Item; readonly input: AcceptInput }>,
  id: NewId = newId,
): Command | null => {
  const [first, ...rest] = items.flatMap(({ item, input }) => acceptChanges(item, input))
  return first
    ? changeset(id, items.length === 1 ? `Accept ${items[0]?.item.ref}` : `Accept ${items.length} items`, [
        first,
        ...rest,
      ])
    : null
}

/** Close triage items as duplicate of another item, or as won't do (one changeset). */
export const triageCloseCommand = (
  itemIds: ReadonlyArray<string>,
  how: { readonly _tag: "duplicate"; readonly of: string } | { readonly _tag: "wont_do" },
  id: NewId = newId,
): Command | null => {
  const resolution: Resolution =
    how._tag === "duplicate" ? { _tag: "duplicate", of: how.of as never } : { _tag: "wont_do" }
  const note = how._tag === "duplicate" ? "duplicate" : "won't do"
  const [first, ...rest] = itemIds.map(
    (itemId): GraphCommand => ({
      _tag: "CloseItem",
      itemId: itemId as never,
      as: "cancelled",
      note,
      resolution,
    }),
  )
  return first ? changeset(id, `Triage: ${note}`, [first, ...rest]) : null
}

/** What the Mark done form offers as evidence: the item's command checks, its artifacts and last run. */
export interface MarkDoneOptions {
  /** `command` checks: the person confirms each one passed (all required). */
  readonly commandChecks: ReadonlyArray<string>
  /** Kinds of `artifact` checks: an artifact of each kind is needed. */
  readonly artifactKinds: ReadonlyArray<string>
  /** The item's artifacts that were not rejected, newest first. */
  readonly artifacts: ReadonlyArray<{ readonly id: string; readonly title: string; readonly kind: string }>
  readonly lastRun: { readonly id: string; readonly ref: string } | null
  /** Evidence is required (command or artifact checks); otherwise a note is enough. */
  readonly evidenceRequired: boolean
  /**
   * Decision 0101: the result the close form preselects: the newest worker's `report_outcome` result, else
   * "report delivered" for a read-only item, else "verified".
   */
  readonly defaultResult: CloseResult
}

export const markDoneOptions = (state: ProjectState, itemId: string): MarkDoneOptions => {
  const item = state.items.find((t) => t.id === itemId)
  if (!item)
    return {
      commandChecks: [],
      artifactKinds: [],
      artifacts: [],
      lastRun: null,
      evidenceRequired: false,
      defaultResult: "verified",
    }
  const run = item.lastRunId ? state.runs.find((r) => r.id === item.lastRunId) : undefined
  const commandChecks = item.checks.flatMap((c) => (c._tag === "command" ? [c.name] : []))
  const artifactKinds = item.checks.flatMap((c) => (c._tag === "artifact" ? [c.kind] : []))
  return {
    commandChecks,
    artifactKinds,
    artifacts: state.artifacts
      .filter((a) => a.itemId === itemId && a.state !== "rejected")
      .sort((a, b) => b.createdAt - a.createdAt)
      .map((a) => ({ id: a.id, title: a.title, kind: a.kind })),
    lastRun: item.lastRunId ? { id: item.lastRunId, ref: run?.ref ?? item.lastRunId } : null,
    evidenceRequired: commandChecks.length > 0 || artifactKinds.length > 0,
    defaultResult: reportedResult(state, itemId) ?? (item.readOnly ? "report_delivered" : "verified"),
  }
}

/** The newest done claim's `result` among the item's worker runs (accepted outcome, else the latest claim). */
const reportedResult = (state: ProjectState, itemId: string): CloseResult | null => {
  const runs = state.runs
    .filter((r) => r.itemId === itemId && r.kind === "worker")
    .sort((a, b) => b.requestedAt - a.requestedAt)
  for (const r of runs) {
    const done = [r.outcome, r.claimed].find((o) => o?._tag === "done")
    if (done?._tag === "done" && done.result) return done.result
  }
  return null
}

/**
 * The evidence of a person's Mark done: one `run` per picked run, one `artifact` per picked artifact (only the item's
 * own), one passed `check` per ticked command check ("confirmed by a person").
 */
export const markDoneEvidence = (
  state: ProjectState,
  itemId: string,
  picks: {
    readonly runIds: ReadonlyArray<string>
    readonly artifactIds: ReadonlyArray<string>
    readonly passedChecks: ReadonlyArray<string>
  },
): Evidence[] => {
  const options = markDoneOptions(state, itemId)
  const runs = new Set(state.runs.filter((r) => r.itemId === itemId).map((r) => r.id as string))
  if (options.lastRun) runs.add(options.lastRun.id)
  const artifacts = new Set(options.artifacts.map((a) => a.id))
  const checks = new Set(options.commandChecks)
  const unique = <T>(xs: ReadonlyArray<T>) => [...new Set(xs)]
  return [
    ...unique(picks.runIds)
      .filter((runId) => runs.has(runId))
      .map((runId): Evidence => ({ _tag: "run", runId: runId as never })),
    ...unique(picks.artifactIds)
      .filter((artifactId) => artifacts.has(artifactId))
      .map((artifactId): Evidence => ({ _tag: "artifact", artifactId: artifactId as never })),
    ...unique(picks.passedChecks)
      .filter((name) => checks.has(name))
      .map((name): Evidence => ({ _tag: "check", name, passed: true, detail: "confirmed by a person" })),
  ]
}

/** The agent's pause is a graph edit (SetAgentStatus), as the Needs-you "Pause @agent" option does it. */
export const setAgentStatusCommand = (
  agentId: string,
  status: "active" | "paused" | "retired",
  id: NewId = newId,
): Command =>
  changeset(
    id,
    status === "paused" ? "Pause the agent" : status === "retired" ? "Retire the agent" : "Resume the agent",
    [{ _tag: "SetAgentStatus", agentId: agentId as never, status }],
  )

// ---- settings wave (spec §7.1): every write as the signed-in person over W1 ----------------------------------------

/** `description` is not here: the brief changes only through `ReviseBrief` (decision 0049, `reviseBriefCommand`). */
export type ProjectPatch = {
  readonly name?: string
  readonly status?: "active" | "paused" | "archived"
  readonly repos?: ReadonlyArray<string>
  readonly wip?: { readonly maxNow: number }
  readonly workers?: WorkerSettings
  readonly routing?: import("@minga/protocol").RoutingPolicy
  readonly baseBranches?: ReadonlyArray<RepoBaseBranch>
  /** Wave C (decision 0071): the time zone and the daily check's hour, saved whole (project managers). */
  readonly cadence?: Cadence
  /** Decision 0096: the project's sandbox allowlist, saved whole (project managers). */
  readonly allow?: ReadonlyArray<import("@minga/protocol").AllowEntry>
  /** Decision 0129: `private` (members only) or `workspace` (every workspace member sees it). Project admins. */
  readonly visibility?: "workspace" | "private"
}

/** General, status, archive, repos, base branches and presets: one steering command (not a plan edit). */
export const updateProjectCommand = (patch: ProjectPatch): Command => ({
  _tag: "UpdateProject",
  patch: patch as Extract<Command, { _tag: "UpdateProject" }>["patch"],
})

/**
 * Edit the brief (decision 0049): the whole new text, against `baseHash` = core `briefHash` of the text the edit
 * started from. A brief changed meanwhile is refused (Invalid at `baseHash`) instead of overwritten.
 */
export const reviseBriefCommand = (body: string, baseHash: string, id: NewId = newId): Command =>
  changeset(id, "Edit the brief", [{ _tag: "ReviseBrief", body: body as never, baseHash: baseHash as never }])

type GraphOf<T extends GraphCommand["_tag"]> = Extract<GraphCommand, { _tag: T }>

export interface HireInput {
  readonly role: "role" | "reviewer"
  readonly areaId: string | null
  readonly name: string
  readonly handle: string
  readonly title: string
  readonly mission: string
  readonly body: string
  readonly mind: Mind
}

/**
 * Hire an agent (§3.6): a role agent (the `role` policy preset) or a reviewer (the `reviewer` preset), with
 * client-minted agent and policy ids. The new agent's hands stay empty: worker presets decide how its workers work.
 */
export const hireAgentCommand = (input: HireInput, id: NewId = newId): Command =>
  changeset(id, `Hire ${input.name.trim()}`, [
    {
      _tag: "HireAgent",
      agentId: id("agt") as never,
      role: "role",
      areaId: input.areaId as never,
      name: input.name.trim(),
      handle: input.handle as never,
      title: input.title.trim(),
      charter: { mission: input.mission.trim(), body: input.body },
      mind: input.mind,
      hands: { agent: null, model: null, effort: null },
      policyId: id("pol") as never,
      policyPreset: input.role === "reviewer" ? "reviewer" : "role",
    },
  ])

export const updateAgentCommand = (
  agentId: string,
  patch: GraphOf<"UpdateAgent">["patch"],
  rationale = "Update the agent",
  id: NewId = newId,
): Command => changeset(id, rationale, [{ _tag: "UpdateAgent", agentId: agentId as never, patch }])

export const reviseCharterCommand = (
  agentId: string,
  charter: { readonly mission: string; readonly body: string },
  id: NewId = newId,
): Command =>
  changeset(id, "Revise the agent's charter", [
    {
      _tag: "ReviseAgentCharter",
      agentId: agentId as never,
      charter: { mission: charter.mission.trim(), body: charter.body },
    },
  ])

/** Take over an agent: it becomes yours; `mind` (one of your runners) replaces a mind pinned to someone else's. */
export const takeOverAgentCommand = (agentId: string, mind?: Mind, id: NewId = newId): Command =>
  changeset(id, "Take over the agent", [
    { _tag: "TakeOverAgent", agentId: agentId as never, ...(mind ? { mind } : {}) },
  ])

export const updateAreaCommand = (
  areaId: string,
  patch: GraphOf<"UpdateArea">["patch"],
  id: NewId = newId,
): Command => changeset(id, "Update the area", [{ _tag: "UpdateArea", areaId: areaId as never, patch }])

export const reviseAreaCharterCommand = (
  areaId: string,
  charter: { readonly mission: string; readonly body: string },
  id: NewId = newId,
): Command =>
  changeset(id, "Revise the area's charter", [
    {
      _tag: "ReviseAreaCharter",
      areaId: areaId as never,
      charter: { mission: charter.mission.trim(), body: charter.body },
    },
  ])

/** Edit an area in one changeset: its patch and, when changed, its charter (mission + body). */
export const editAreaCommand = (
  areaId: string,
  patch: GraphOf<"UpdateArea">["patch"] | null,
  charter: { readonly mission: string; readonly body: string } | null,
  id: NewId = newId,
): Command | null => {
  const changes: GraphCommand[] = []
  if (patch && Object.keys(patch).length > 0)
    changes.push({ _tag: "UpdateArea", areaId: areaId as never, patch })
  if (charter)
    changes.push({
      _tag: "ReviseAreaCharter",
      areaId: areaId as never,
      charter: { mission: charter.mission.trim(), body: charter.body },
    })
  const [first, ...rest] = changes
  return first ? changeset(id, "Update the area", [first, ...rest]) : null
}

/** Edit an agent in one changeset: identity/area/wake patch and, when changed, its charter. */
export const editAgentCommand = (
  agentId: string,
  patch: GraphOf<"UpdateAgent">["patch"] | null,
  charter: { readonly mission: string; readonly body: string } | null,
  id: NewId = newId,
): Command | null => {
  const changes: GraphCommand[] = []
  if (patch && Object.keys(patch).length > 0)
    changes.push({ _tag: "UpdateAgent", agentId: agentId as never, patch })
  if (charter)
    changes.push({
      _tag: "ReviseAgentCharter",
      agentId: agentId as never,
      charter: { mission: charter.mission.trim(), body: charter.body },
    })
  const [first, ...rest] = changes
  return first ? changeset(id, "Update the agent", [first, ...rest]) : null
}

export const archiveAreaCommand = (areaId: string, id: NewId = newId): Command =>
  changeset(id, "Archive the area", [{ _tag: "ArchiveArea", areaId: areaId as never }])

/** The lead policy switch writes a new revision of the policy document (steering, not a plan edit). */
export const revisePolicyCommand = (
  policyId: string,
  revision: number,
  document: PolicyDocument,
): Command => ({
  _tag: "RevisePolicy",
  policyId: policyId as never,
  revision,
  document,
})

/** The worker preset of an item a new worker does: changes the next attempt only (UpdateItem{assignee}). */
export const itemPresetCommand = (itemId: string, preset: string | null, id: NewId = newId): Command =>
  changeset(id, "Change the worker preset", [
    {
      _tag: "UpdateItem",
      itemId: itemId as never,
      patch: { assignee: { _tag: "worker", preset: preset as never } },
    },
  ])

/**
 * Undo an applied plan from the chat (Wave A §5, §10.3): core's forward-only inverse of changeset `target`, as the
 * person. Never a card (the forced approvals apply to agents only); a conflict comes back as a plain-words Invalid.
 */
export const undoChangesetCommand = (target: string, id: NewId = newId): Command => ({
  _tag: "UndoChangeset",
  changesetId: id("chg") as never,
  target: target as never,
})

/**
 * What the chat's Undo shows for its result: "Undone" when it applied, or when core says the change was already undone
 * (another member did, or the page was reloaded since: nothing is wrong); else the rejection in plain words.
 */
export const undoOutcome = (
  result: CommandResult,
  names?: Parameters<typeof rejectionText>[1],
): { readonly _tag: "done" | "error"; readonly text: string } => {
  const original = result._tag === "Deduplicated" ? result.original : result
  if (original._tag !== "Rejected")
    // A person's undo never becomes a card (§5 item 4); say so truthfully if it ever does.
    return { _tag: "done", text: original._tag === "Proposed" ? "Waits on a card" : "Undone" }
  const r = original.rejection
  if (r._tag === "Invalid" && r.issues.some((i) => i.message === ALREADY_UNDONE))
    return { _tag: "done", text: "Undone" }
  return { _tag: "error", text: rejectionText(r, names) }
}

/**
 * A readable sentence for a rejected command (shown inline next to what failed). `WipLimit` is no longer produced
 * (Wave A: WIP is a warning); its text stays for results stored before.
 */
export const rejectionText = (
  rejection: {
    readonly _tag: string
    readonly [k: string]: unknown
  },
  names?: {
    /** Area ids → names, for "Growth already has 1 Now item (limit 1)". */
    readonly areas?: ReadonlyMap<string, string>
    /** The ref-miss hint ("DIC-12 doesn't exist. … T-12 exists"): core `refMissHint`. */
    readonly refHint?: (ref: string) => string | null
  },
): string => {
  switch (rejection._tag) {
    case "Invalid": {
      const issues = (rejection.issues as ReadonlyArray<{ readonly message: string }> | undefined) ?? []
      return issues.map((i) => i.message).join("; ") || "invalid"
    }
    case "Forbidden":
      return String(rejection.detail ?? "not allowed")
    case "StaleProposal":
      return "This changed since you opened it. Look at it again."
    case "WipLimit": {
      const scope = rejection.scope as { readonly _tag: string; readonly areaId?: string } | undefined
      const where =
        scope?._tag === "area" ? (names?.areas?.get(String(scope.areaId)) ?? "This area") : "The project"
      const limit = Number(rejection.limit)
      return `${where} already has ${String(rejection.active)} Now item${Number(rejection.active) === 1 ? "" : "s"} (limit ${limit})`
    }
    case "Duplicate":
      return String(rejection.reason ?? "already exists")
    case "PreconditionFailed":
      return "Someone changed this in the meantime. Try again."
    case "NotFound": {
      const ref = rejection.ref as { readonly _tag?: string; readonly id?: string } | undefined
      const hint =
        ref?.id && /^[A-Za-z][A-Za-z0-9]*-[1-9][0-9]*$/.test(ref.id) && names?.refHint
          ? names.refHint(ref.id)
          : null
      return hint ?? "It no longer exists."
    }
    case "Conflict":
      return String(rejection.reason ?? "conflict")
    default:
      return rejection._tag
  }
}

/** The names `rejectionText` uses, from the project state: area names and the ref-miss hint (§1.1). */
export const rejectionNames = (state: ProjectState) => ({
  areas: new Map(state.areas.map((a) => [a.id as string, a.name])),
  refHint: (ref: string) => refMissHint(state, ref),
})

/** Stop a live run (Activity sheet, inspector): the item blocks with a truthful `interrupted` and wakes its owner. */
export const cancelRunCommand = (runId: string, reason = "stopped by a person"): Command => ({
  _tag: "CancelRun",
  runId: runId as never,
  reason,
})

/**
 * Wave B2 (decision 0057): "Answer and close" on a question item, as commands to send in order. With an open question
 * card about the item, the card is decided with the chosen option and the text (so it leaves Needs you), then the item
 * closes; without one, the answer is a decision comment on the item, then the item closes. The answer lands in
 * Decisions and in Lea's next prompt, and the close releases what the question `blocks`. The last command is always
 * the CloseItem: after a failed close, a retry sends only that one (never a second answer or comment).
 */
export const answerQuestionCommands = (
  itemId: string,
  text: string,
  card?: {
    readonly proposal: Pick<Proposal, "id" | "revision" | "contentHash">
    readonly optionKey: string | null
  },
  id: NewId = newId,
): Command[] => {
  const answer = text.trim()
  const first: Command = card
    ? {
        _tag: "DecideProposal",
        proposalId: card.proposal.id,
        revision: card.proposal.revision,
        contentHash: card.proposal.contentHash,
        decision: { _tag: "answer", optionKey: card.optionKey, text: answer || null },
      }
    : {
        _tag: "PostComment",
        commentId: id("cmt") as never,
        target: { _tag: "item", id: itemId } as never,
        threadId: null,
        body: answer as never,
        decision: true,
      }
  return [first, closeItemCommand(itemId, "done", answer || "Answered", {}, id)]
}

// ---- wave C (decision 0071): memory, monitors, the agent page ---------------------------------------------------------

/** A person's direct write (spec §4.3: people's writes apply at once): create, revise (Edit, Restore), archive (Forget). */
export const writeMemoryCommand = (
  op: "create" | "revise" | "archive" | "revoke",
  itemId: string,
  draft: MemoryDraft | null,
  reason: string | null = null,
): Command => ({ _tag: "WriteMemory", itemId: itemId as never, op, draft, reason })

/** Teach (and Remember this): a new memory with a client-minted id; decide numbers it `M-n`. */
export const teachMemoryCommand = (draft: MemoryDraft, id: NewId = newId): Command =>
  writeMemoryCommand("create", id("mem"), draft)

/** Edit: a new revision with your name; the ref stays. */
export const editMemoryCommand = (itemId: string, draft: MemoryDraft): Command =>
  writeMemoryCommand("revise", itemId, draft)

/** Forget: archived; agents stop loading and finding it, its page stays. */
export const forgetMemoryCommand = (itemId: string): Command =>
  writeMemoryCommand("archive", itemId, null, "forgotten by a person")

/** The draft fields of a stored memory (what a Restore or an unchanged Edit writes back). */
export const draftOfMemory = (m: MemoryItem): MemoryDraft => ({
  scope: m.scope,
  title: m.title,
  body: m.body,
  content: m.content,
  triggers: m.triggers,
  locked: m.locked,
  ...(m.source !== undefined ? { source: m.source } : {}),
})

/** Restore a forgotten memory: a revise with its own text (a new revision; ref, source and taint stay, core). */
export const restoreMemoryCommand = (m: MemoryItem): Command => editMemoryCommand(m.id, draftOfMemory(m))

/** Add or replace (Edit) a monitor; decide stamps the setter. Accepting an agent's card decides the card instead. */
export const setMonitorCommand = (monitor: Monitor): Command => ({ _tag: "SetMonitor", monitor })

export const removeMonitorCommand = (monitorId: string): Command => ({
  _tag: "RemoveMonitor",
  monitorId: monitorId as never,
})

export const setMonitorPausedCommand = (monitorId: string, paused: boolean): Command => ({
  _tag: "SetMonitorPaused",
  monitorId: monitorId as never,
  paused,
})

/** A blocked monitor's Resume as me: you become its setter (its turns are asked by you). */
export const resumeMonitorCommand = (monitorId: string): Command => ({
  _tag: "ResumeMonitor",
  monitorId: monitorId as never,
})

/** The agent's daily cap of timed turns (keeper or admin). */
export const timedCapCommand = (agentId: string, cap: number | null): Command =>
  updateAgentCommand(agentId, { timedCap: cap }, "Change the daily cap")

export const chainCapCommand = (agentId: string, cap: number | null): Command =>
  updateAgentCommand(agentId, { chainCap: cap }, "Change coordination budget")

/** Tell Lea: a comment on the item that @mentions her (it wakes her as a direct mention). */
export const tellAgentCommand = (itemId: string, handle: string, text: string, id: NewId = newId): Command =>
  postCommentCommand({ _tag: "item", id: itemId }, `@${handle} ${text.trim()}`, undefined, id)
